
import { Star } from './icons/Star'
import { Valet } from './icons/Valet'
import { Calendar } from './icons/Calendar'
import { Lightning } from './icons/Lightning'
import { Revert } from './icons/Revert'
import { Tbank } from './icons/Tbank'
import { cn } from '~shared/utils/cn'
import { getProgramData } from '~shared/api/single-program-data/single-program-data'
import { Button } from '~shared/components/button-v2'
import { LeadFormDialog } from '~widgets/lead-form/components/lead-form-dialog'
import React from 'react'
import { calculatePrice } from '~shared/utils/calculatePrice'
import { useAt } from '~shared/hooks/useAt'
import { useCalculateClosestEnrollment } from '~shared/hooks/useCalculateClosestEnrollment'
import { useCalculateStudyDuration } from '~shared/hooks/useCalculateStudyDuration'
import { insertThousandsSeparator } from '~shared/utils/insert-thousands-separator'
import { getEffectiveDiscount } from '~shared/utils/discount'

export const CostSection = ({
  program
}: {
  program: Awaited<ReturnType<typeof getProgramData>>
}) => {
  const isCourse = ['kursy'].includes(program?.category?.slug ?? '')
  const { isMba } = useAt()

  const effectiveDiscount =
    getEffectiveDiscount({
      formatDiscount: program?.cost?.discount,
      category: program?.category,
      faculty: program?.faculty
    }) || 0

  const calculatedClosestEnrollment = useCalculateClosestEnrollment({
    timestamp: program?.cost?.closestEnrollment,
    isCourse
  })
  const calculatedStudyDuration = useCalculateStudyDuration({
    years: program?.cost?.studyDurationYears,
    months: program?.cost?.studyDurationMonths
  })
  const calculatedPrice = calculatePrice({
    price: program?.cost?.price || 0,
    discount: effectiveDiscount,
    discountMonths: program?.cost?.discountMonths || 12,
    locale: 'ru',
    semestersNumber: isMba ? 1 : 2
  })

  const whatIncludes = [
    {
      id: 1,
      title: 'Дистанционно',
      description: 'Обучение из любой точки',
      icon: <Star />
    },
    {
      id: 2,
      title: 'Налоговый вычет',
      description: 'Поможем вернуть 13%',
      icon: <Valet />
    },
    {
      id: 3,
      title: calculatedStudyDuration,
      description: 'Длительность обучения',
      icon: <Calendar />
    },
    {
      id: 4,
      title: calculatedClosestEnrollment,
      description: 'Ближайшее зачисление',
      icon: <Lightning />
    },
    {
      id: 5,
      title: 'Полный возврат',
      description: 'В течение первых двух недель, если передумаете',
      icon: <Revert />
    },
    {
      id: 6,
      title: 'Рассрочка от\u00A0Т\u2011Банка',
      description: 'Без переплат',
      icon: <Tbank />
    }
  ]
  const [isOpen, setIsOpen] = React.useState(false)
  return (
    <section className='bg-black py-[80px] text-white'>
      <div
        className={cn(
          'mx-auto px-[16px] box-border',
          'max-w-[672px]',
          'md:max-w-[832px]',
          '2lg:max-w-[1280px]'
        )}>
        <h2
          className={cn(
            'mb-[24px] text-[40px] font-medium',
            'md:text-[48px] md:mb-[32px]',
            '2lg:mb-[56px] 2lg:text-[64px]'
          )}>
          Стоимость обучения
        </h2>
        <div
          className={cn(
            'bg-[#18191A] p-[16px] gap-[48px] rounded-[16px] justify-between',
            'md:p-[32px]',
            '2lg:flex 2lg:rounded-[32px]'
          )}>
          <div className={cn('mb-[24px] 2lg:mb-0', 'md:mb-[60px]')}>
            <div
              className={cn(
                'flex gap-[24px] items-center mb-[24px]',
                'md:mb-[32px]',
                '2lg:mb-[46px]'
              )}>
              <p
                className={cn(
                  'text-[14px]/[120%] 2lg:text-[24px]/[120%] font-medium',
                  'md:text-[20px]/[120%]',
                  '2lg:text-[24px]/[120%]'
                )}>
                При рассрочке на {program?.cost?.discountMonths || 12} месяцев
              </p>
              <span className='h-[35px] bg-[#2A2A2A] rounded-[55px] px-[16px] flex items-center'>
                {program?.category?.name}
              </span>
            </div>
            <p className='bg-[#702323] text-[14px] font-medium h-[35px] rounded-[55px] px-[16px] flex items-center w-fit'>
              {effectiveDiscount
                ? `Скидка ${effectiveDiscount}%`
                : 'Скидка 10% при оплате всей суммы сразу'}
            </p>
            <p
              className={cn(
                'text-[32px] font-medium my-[16px]',
                'md:text-[48px]',
                'lg:text-[80px]'
              )}>
              {insertThousandsSeparator(calculatedPrice?.yearInstallmentPlan?.price)}
              <span
                className={cn(
                  'text-[16px]',
                  'md:text-[32px]',
                  'lg:text-[48px]'
                )}>
                {' '}
                ₽/мес
              </span>
              {effectiveDiscount ? (
                <span
                  className={cn(
                    'text-[14px] line-through text-white/60 ml-4',
                    'md:text-[20px]'
                  )}>
                  {insertThousandsSeparator(
                    calculatedPrice?.yearInstallmentPlan?.regularPrice
                  )}{' '}
                  ₽/мес
                </span>
              ) : null}
            </p>
            <p
              className={cn(
                'text-[14px] font-medium text-white/60 mb-[32px]',
                'md:text-[20px]'
              )}>
              или {insertThousandsSeparator(program?.cost?.priceOneTime ?? calculatedPrice.total.price)}₽{' '}
              {program?.cost?.priceOneTime
                ? 'за год при оплате сразу'
                : isMba || isCourse
                  ? effectiveDiscount
                    ? 'одним платежом с учетом скидки'
                    : 'одним платежом'
                  : effectiveDiscount
                    ? 'за год с учетом скидки'
                    : 'за год'}
            </p>
            <Button
              onClick={() => setIsOpen(true)}
              className='rounded-[16px] duration-200 ease-in-out justify-center bg-accent text-white flex items-center h-[50px] px-[16px] hover:bg-black'>
              Записаться на курс
            </Button>
            <LeadFormDialog
              open={isOpen}
              onOpenChange={setIsOpen}
              program={{
                productName: program?.name ?? '',
                productId: program?.productUid ?? null,
                productCategory: program?.category?.name ?? '',
                productFaculty: program?.faculty?.name ?? '',
                institute: program?.institute?.name ?? ''
              }}
            />
          </div>
          {/* что включено */}
          <div
            className={cn(
              'grid grid-cols-1 gap-[8px]',
              'md:grid-cols-2 md:gap-[16px]'
            )}>
            {whatIncludes.map(el => (
              <div
                key={el.id}
                className={cn(
                  'flex gap-[12px] bg-[#2A2A2A] p-[16px] rounded-[24px]',
                  'md:p-[20px] md:rounded-[32px]',
                  '2lg:max-w-[272px]'
                )}>
                <div className='mt-[4px]'>{el.icon}</div>
                <div>
                  <p className='text-[20px] font-medium mb-[8px]'>{el.title}</p>
                  <p className='text-white/60 text-[14px]'>{el.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}
